import type {
  AgentRunInput,
  CommandResult,
} from '@travel-planner/shared/schemas';
import type { ReactNode } from 'react';
import type { AgentPanelMode } from '../model/use-agent-panel-store';
import type { AgentPanelFooterStatus } from './agent-panel-footer';
import { AlertCircle, Mic, Sparkles } from 'lucide-react';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { trpc, trpcVanilla } from '@/shared/api/trpc';
import { pickCss } from '@/shared/lib/css-utils';
import { Alert, Button, Text, toastStore } from '@/shared/ui';
import { useAgentPanelAdapter } from '../model/adapter-context';
import { isPageActionResult } from '../model/page-actions';
import { useAgentCommand } from '../model/use-agent-command';
import { useAgentKeyboard } from '../model/use-agent-keyboard';
import {
  AGENT_PANEL_MODE,
  useAgentPanelStore,
} from '../model/use-agent-panel-store';
import { useAgentRun } from '../model/use-agent-run';
import { useAgentTranscriptStore } from '../model/use-agent-transcript-store';
import { useAgentVoice, voiceErrorMessage } from '../model/use-agent-voice';
import { useAgentWorkflow } from '../model/use-agent-workflow';
import { usePageActionFlashStore } from '../model/use-page-action-flash';
import { VoiceContext } from '../model/voice-context';
import { AgentDrawerHeader } from './agent-drawer-header';
import css from './agent-panel.module.scss';
import { AgentPanelCapabilities } from './agent-panel-capabilities';
import { AgentPanelFooter, FOOTER_STATUS } from './agent-panel-footer';
import { AgentRunProgress } from './agent-run-progress';
import { AgentSearchBar } from './agent-search-bar';
import { CommandSessionPanel, HistoryView } from './command-session-panel';
import { GeneratorPanel } from './generator/generator-panel';
import { NavigateHandoffCard } from './navigate-handoff-card';
import { PageActionStatus } from './page-action-status';
import { WorkflowStepper } from './workflow-stepper';

const styles = pickCss(css, css.root);

// Feature switch (#339): the dynamic Loop Engine is the only agent surface.
// The scripted command/workflow planner + hooks are kept in the codebase but
// no longer reachable from the UI. Flip to `false` to restore the old toggle
// and planner exactly as before.
const DYNAMIC_AGENT_ONLY = true;

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

export type AgentPanelProps = {
  title?: string;
  children?: ReactNode;
};

export const AgentPanel = ({ title = 'Agent', children }: AgentPanelProps) => {
  // The adapter is provided by the active page via <AdapterContext.Provider>.
  // Pages without an adapter render the empty-state `children` fallback.
  const adapter = useAgentPanelAdapter();
  const mode = useAgentPanelStore((s) => s.mode);
  const close = useAgentPanelStore((s) => s.close);
  const reset = useAgentPanelStore((s) => s.reset);
  const query = useAgentPanelStore((s) => s.query);
  const setQuery = useAgentPanelStore((s) => s.setQuery);
  const setMode = useAgentPanelStore((s) => s.setMode);

  const titleId = useId();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const transcribeMutation = trpc.agentPanel.transcribe.useMutation();
  const commandStepMutation = trpc.agentPanel.commandStep.useMutation();
  const commandExecuteMutation = trpc.agentPanel.commandExecute.useMutation();
  const workflowStartMutation = trpc.agentPanel.workflowStart.useMutation();
  const workflowAdvanceMutation = trpc.agentPanel.workflowAdvance.useMutation();
  const commandSession = useAgentPanelStore((s) => s.commandSession);
  const workflowSession = useAgentPanelStore((s) => s.workflowSession);
  const pendingNavigation = useAgentPanelStore((s) => s.pendingNavigation);
  const workflow = useAgentWorkflow({
    adapter,
    startMutate: workflowStartMutation.mutateAsync,
    advanceMutate: workflowAdvanceMutation.mutateAsync,
  });
  const command = useAgentCommand({
    adapter,
    stepMutate: commandStepMutation.mutateAsync,
    executeMutate: commandExecuteMutation.mutateAsync,
    workflow: { startWorkflow: workflow.startWorkflow },
  });

  // Dynamic Agent Loop Engine (#276). Opt-in surface: when on, the top input
  // drives the multi-step `runAgent` loop (chain tools, live step progress,
  // undo for writes) instead of the scripted command/workflow planner. Kept
  // local + additive so the existing guided flow is untouched when off.
  //
  // Loop tools that return a page-action result (query_entity's apply_filters)
  // are applied to the page here, the same way the scripted flow's
  // `handlePageActionResult` does — without the auto-close, since the run is
  // still streaming its progress in the panel.
  const onLoopStepResult = useCallback(
    (result: CommandResult) => {
      adapter?.onCommandResult?.(result);
      if (isPageActionResult(result)) {
        usePageActionFlashStore.getState().triggerFlash();
      }
    },
    [adapter]
  );
  const agentRun = useAgentRun({ onStepResult: onLoopStepResult });
  const [dynamicMode, setDynamicMode] = useState(DYNAMIC_AGENT_ONLY);
  const isRunning = agentRun.status === 'running';

  // Cross-turn undo (P2.6): a persistent control, shown whenever this page
  // session still has a reversible change. Targets the server's session-keyed
  // stack (peekLatest) so it works across turns, not just the last run.
  const undoSessionId = useAgentTranscriptStore((s) => s.sessionId);
  const hasUndoableChange = useAgentTranscriptStore((s) =>
    s.turns.some((t) => t.undoToken != null)
  );
  const onUndoLastChange = useCallback(async () => {
    if (!undoSessionId) return;
    try {
      const { undone, token } =
        await trpcVanilla.agentPanel.agentUndoLast.mutate({
          sessionId: undoSessionId,
        });
      const reverted = token !== null && undone > 0;
      // Only clear a client turn when the server actually reversed something,
      // so a no-op click doesn't drop an entry that's still undoable.
      if (reverted) useAgentTranscriptStore.getState().markLatestUndone();
      toastStore.getState().show({
        message: reverted
          ? 'Reverted the last change.'
          : 'Nothing left to undo.',
      });
    } catch {
      toastStore.getState().show({
        message: "Couldn't undo — the change may already have been reversed.",
      });
    }
  }, [undoSessionId]);

  const runDynamic = useCallback(
    (goal: string) => {
      if (!adapter) return;
      // Same `{ contextType, ...context }` shape the command flow builds —
      // the discriminated `AgentRunInput` is satisfied by the adapter's
      // statically-known per-context fields.
      void agentRun.run({
        contextType: adapter.contextType,
        ...adapter.context,
        goal,
        // Live page context (#274): filters, columns, selection, route,
        // permissions, extras. Lets the loop resolve "this"/"these"/"sort
        // this". Undefined for pages that don't yet build a snapshot.
        ...(adapter.snapshot ? { snapshot: adapter.snapshot } : {}),
      } as AgentRunInput);
    },
    [adapter, agentRun]
  );

  const [lastVoiceTranscript, setLastVoiceTranscript] = useState<string | null>(
    null
  );

  // The top search input routes everything through the command/workflow
  // planner. Typing "Plan a trip" starts plan_trip; "find a sushi place in
  // Oslo" opens find_product; every command is reachable the same way. Keep
  // onChange a pure store update so React stays in control of the input; do
  // not auto-fire on keystroke.
  const onChange = useCallback(
    (value: string) => {
      setQuery(value);
      setMode(value.trim() ? AGENT_PANEL_MODE.Typing : AGENT_PANEL_MODE.Idle);
    },
    [setMode, setQuery]
  );

  // Single top input — dual-purpose. When the LLM is collecting a follow-up
  // answer the same submit drives `sendTurn`; otherwise it kicks off a new
  // command session. The prior "Type your answer…" form below the
  // conversation has been removed; everything routes through this input.
  const onSubmit = useCallback(() => {
    const current = useAgentPanelStore.getState().query.trim();
    if (!current) return;
    setQuery('');
    // Dynamic mode bypasses the scripted planner entirely — one goal in, the
    // loop chains tools to satisfy it.
    if (dynamicMode) {
      runDynamic(current);
      return;
    }
    const session = useAgentPanelStore.getState().commandSession;
    if (session && session.phase === 'collecting') {
      void command.sendTurn(current);
      return;
    }
    void command.startSession(current);
  }, [command, setQuery, dynamicMode, runDynamic]);

  // Show the LLM's pending question as the input placeholder during a
  // follow-up turn so the consultant always knows what they're answering.
  // The same question is also the latest assistant bubble in the log above.
  const searchPlaceholder =
    commandSession?.phase === 'collecting' && commandSession.pendingQuestion
      ? commandSession.pendingQuestion
      : undefined;

  const voice = useAgentVoice({
    // Voice transcription fills the input only — the consultant reviews
    // the heard text and presses Enter to submit. Auto-submitting on mic
    // release would race the consultant's chance to correct mishearings.
    onTranscribed: useCallback(
      (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        setLastVoiceTranscript(text);
        onChange(trimmed);
        searchInputRef.current?.focus();
      },
      [onChange]
    ),
    transcribeMutate: transcribeMutation.mutateAsync,
  });

  const isMicSupported =
    typeof MediaRecorder !== 'undefined' && !voice.isMicDenied;

  const isGenerator = mode === AGENT_PANEL_MODE.Generator;

  const focusSearch = useCallback(() => {
    // Slash-to-focus is meaningless when the search bar is hidden — let
    // the keystroke through so the textarea / form fields keep it.
    if (isGenerator) return;
    searchInputRef.current?.focus();
  }, [isGenerator]);

  const focusNext = useCallback(() => {
    moveFocusInList(bodyRef.current, 1);
  }, []);

  const focusPrev = useCallback(() => {
    moveFocusInList(bodyRef.current, -1);
  }, []);

  const commitFocused = useCallback(() => {
    const focused = document.activeElement as HTMLElement | null;
    focused?.click();
  }, []);

  const handleEscape = useCallback(() => {
    // 1. In-flight voice gets cancelled first — abort the recorder /
    //    transcribe request before touching panel state.
    if (voice.phase === 'recording' || voice.phase === 'transcribing') {
      voice.cancel();
      return;
    }
    // Drop any sticky voice-error banner regardless of what comes next.
    voice.clearError();

    // 2. If the panel has anything to undo (active session, typed query,
    //    non-idle mode), snap back to the default empty-state idle view
    //    instead of closing. Esc-to-close still works as a fallback when
    //    the panel is already in the default state.
    const hasSession = !!commandSession || !!workflowSession;
    const hasQuery = query.length > 0;
    const isDefault =
      mode === AGENT_PANEL_MODE.Idle && !hasSession && !hasQuery;
    if (!isDefault) {
      reset();
      return;
    }
    close();
  }, [voice, mode, commandSession, workflowSession, query, reset, close]);

  const handleChange = useCallback(
    (value: string) => {
      setLastVoiceTranscript(null);
      voice.clearError();
      onChange(value);
    },
    [onChange, voice]
  );

  useAgentKeyboard({
    enabled: mode !== AGENT_PANEL_MODE.Closed,
    onClose: handleEscape,
    onFocusSearch: focusSearch,
    onFocusNext: focusNext,
    onFocusPrev: focusPrev,
    onCommitFocused: commitFocused,
    onPushToTalkStart: useCallback(() => voice.start(), [voice]),
    onPushToTalkEnd: useCallback(() => voice.stop(), [voice]),
  });

  const isRecording = voice.phase === 'recording';

  const headerSubtitle = useMemo(() => {
    if (isRecording) {
      return (
        <span className={css.listeningSubtitle}>
          <span className={css.listeningDot} />
          <Text as="span" size="xs" weight="medium">
            Listening &middot; {formatElapsed(voice.elapsedMs)}
          </Text>
        </span>
      );
    }
    return adapter?.subtitle;
  }, [isRecording, voice.elapsedMs, adapter?.subtitle]);

  if (mode === AGENT_PANEL_MODE.Closed) return null;

  return (
    <VoiceContext.Provider value={voice}>
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className={styles({ mode })}
        role="dialog"
      >
        <button
          aria-label="Dismiss agent panel"
          className={css.backdrop}
          onClick={close}
          type="button"
        />
        <section className={css.panel}>
          <AgentDrawerHeader
            onClear={
              commandSession || workflowSession || query.length > 0
                ? reset
                : undefined
            }
            onClose={close}
            subtitle={headerSubtitle}
            title={title}
            titleId={titleId}
          />
          <div className={css.body} ref={bodyRef}>
            {dynamicMode && adapter ? (
              agentRun.status === 'idle' ? (
                <div className={css.dynamicHint}>
                  <Text as="p" size="sm">
                    Ask the agent to find or change things on this page. It can
                    chain multiple steps, and write actions can be undone.
                  </Text>
                </div>
              ) : (
                <div className={css.dynamicBody}>
                  {agentRun.goal && (
                    <HistoryView
                      history={[{ role: 'user', content: agentRun.goal }]}
                    />
                  )}
                  <AgentRunProgress
                    activeLabel={agentRun.activeLabel}
                    budgetExhausted={agentRun.budgetExhausted}
                    buildProgress={agentRun.buildProgress}
                    canContinue={agentRun.canContinue}
                    finalText={agentRun.finalText}
                    onConfirm={(callId, decision) =>
                      void agentRun.confirm(callId, decision)
                    }
                    onKeepGoing={() => void agentRun.keepGoing()}
                    resolvedConfirmIds={agentRun.resolvedConfirmIds}
                    status={agentRun.status}
                    timeline={agentRun.timeline}
                  />
                  {hasUndoableChange && !isRunning && (
                    <div className={css.undoLast}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void onUndoLastChange()}
                      >
                        Undo last change
                      </Button>
                    </div>
                  )}
                </div>
              )
            ) : isGenerator ? (
              <GeneratorPanel />
            ) : pendingNavigation ? (
              // Cross-page handoff awaiting consultant confirmation. Renders
              // a confirm card with the planner's reason + Take me there /
              // Cancel buttons. See `handle-navigate-handoff.ts` and the
              // per-page-scoping PRD.
              <NavigateHandoffCard />
            ) : commandSession ? (
              // As long as a session is alive, the conversation is the
              // panel body — even while the consultant is mid-typing a
              // brand-new prompt (`mode === Typing`). Gating the
              // conversation on `mode === CommandSession` made the first
              // keystroke of a follow-up wipe the chat off-screen.
              <>
                {workflowSession && (
                  <WorkflowStepper
                    isPending={workflowAdvanceMutation.isPending}
                    onSkipStep={workflow.skipStep}
                  />
                )}
                <HistoryView history={commandSession.history} />
                <CommandSessionPanel
                  isPending={
                    commandStepMutation.isPending ||
                    commandExecuteMutation.isPending ||
                    workflowStartMutation.isPending ||
                    workflowAdvanceMutation.isPending
                  }
                  onCancel={
                    workflowSession
                      ? () => {
                          workflow.cancel();
                          command.cancel();
                        }
                      : command.cancel
                  }
                  onConfirm={command.confirmAndExecute}
                  onContinueWorkflow={workflow.advanceFromStepResult}
                  onRestartWorkflowStep={workflow.restartStep}
                  onSendTurn={command.sendTurn}
                />
              </>
            ) : adapter ? (
              <AgentPanelCapabilities
                contextType={adapter.contextType}
                onPick={(example) => {
                  // Strip surrounding curly quotes from the example phrase
                  // (the list uses them for readability) before sending to
                  // the planner.
                  const cleaned = example.replace(/^[“"']|[”"']$/g, '').trim();
                  if (!cleaned) return;
                  setQuery(cleaned);
                  void command.startSession(cleaned);
                }}
              />
            ) : (
              children
            )}
          </div>
          {!isGenerator && (
            <div className={css.search}>
              {adapter && !DYNAMIC_AGENT_ONLY && (
                <div className={css.modeToggle}>
                  <Button
                    icon={<Sparkles aria-hidden="true" size={14} />}
                    onClick={() => setDynamicMode((v) => !v)}
                    size="sm"
                    type="button"
                    variant={dynamicMode ? 'primary' : 'ghost'}
                  >
                    {dynamicMode ? 'Dynamic agent: on' : 'Dynamic agent'}
                  </Button>
                </div>
              )}
              <PageActionStatus />
              <AgentSearchBar
                audioLevel={voice.audioLevel}
                inputRef={searchInputRef}
                isListening={isRecording}
                isMicDenied={voice.isMicDenied}
                isMicSupported={isMicSupported}
                isSearching={
                  isRunning ||
                  commandStepMutation.isPending ||
                  commandExecuteMutation.isPending ||
                  workflowStartMutation.isPending ||
                  workflowAdvanceMutation.isPending
                }
                isTranscribing={voice.phase === 'transcribing'}
                onChange={handleChange}
                onMicHoldEnd={() => voice.stop()}
                onMicHoldStart={() => voice.start()}
                onSubmit={onSubmit}
                placeholder={
                  dynamicMode ? 'Ask the agent anything…' : searchPlaceholder
                }
                value={query}
              />
              {lastVoiceTranscript && !isRecording && (
                <div className={css.voiceBanner}>
                  <Mic aria-hidden="true" size={14} />
                  <Text as="span" size="sm" weight="medium">
                    Heard: {lastVoiceTranscript}
                  </Text>
                </div>
              )}
              {voice.errorReason && voice.phase !== 'recording' && (
                <Alert
                  icon={<AlertCircle aria-hidden="true" size={14} />}
                  role="alert"
                  variant="error"
                >
                  {voiceErrorMessage(voice.errorReason)}
                </Alert>
              )}
            </div>
          )}
          <AgentPanelFooter
            status={mapModeToFooterStatus({
              mode,
              voicePhase: voice.phase,
              isWorking:
                isRunning ||
                commandStepMutation.isPending ||
                commandExecuteMutation.isPending ||
                workflowStartMutation.isPending ||
                workflowAdvanceMutation.isPending,
            })}
          />
        </section>
      </div>
    </VoiceContext.Provider>
  );
};

function moveFocusInList(container: HTMLElement | null, delta: 1 | -1) {
  if (!container) return;
  // `[data-suggestion-id]` is the legacy hook on suggest-era result rows.
  // `[data-agent-focusable]` is the new general hook — capability buttons
  // tag themselves with it so ↑↓ navigates the empty-state list. Querying
  // both keeps the suggest-era result renderers (still used for
  // find_product standalone) navigable too.
  const items = Array.from(
    container.querySelectorAll<HTMLElement>(
      '[data-suggestion-id], [data-agent-focusable]'
    )
  );
  if (items.length === 0) return;
  const active = document.activeElement as HTMLElement | null;
  const currentIndex = active ? items.indexOf(active) : -1;
  const nextIndex =
    currentIndex === -1
      ? delta === 1
        ? 0
        : items.length - 1
      : (currentIndex + delta + items.length) % items.length;
  items[nextIndex]?.focus();
}

function mapModeToFooterStatus({
  mode,
  voicePhase,
  isWorking,
}: {
  mode: AgentPanelMode;
  voicePhase: string;
  isWorking: boolean;
}): AgentPanelFooterStatus {
  if (voicePhase === 'recording') return FOOTER_STATUS.Listening;
  // Any in-flight planner / step / execute / workflow mutation flips the
  // footer to "Thinking" so the consultant sees the panel is busy. The
  // `Loading` mode case is preserved for the very first command-step
  // request before commandSession exists yet (empty-state path).
  if (isWorking || mode === AGENT_PANEL_MODE.Loading) {
    return FOOTER_STATUS.Thinking;
  }
  return FOOTER_STATUS.Idle;
}
